// Usage: new HicomMultiColumnComboBox('comboId');
// Implementation of a multi-column combo box with filtering and highlighting of matching text in the items.
class HicomMultiColumnComboBox {
    private comboId: string;
    private highlightOnSearch: boolean;

    constructor(id: string, highlightOnSearch: boolean) {
        this.comboId = id;
        this.highlightOnSearch = highlightOnSearch;
        this.initialize();
    }

    /**
     * Initializes the multi-column combo box by binding the dataBound event to the onFiltering method.
     */
    private initialize(): void {
        const existingComboBox = $(`#${this.comboId}`).data('kendoMultiColumnComboBox') as kendo.ui.MultiColumnComboBox | undefined;
        if (existingComboBox) {
            this.accessibilityInit(existingComboBox);
            if (this.highlightOnSearch) {
                existingComboBox.bind("dataBound", (e: kendo.ui.MultiColumnComboBoxChangeEvent) => {
                    this.onFiltering(e);
                });
            }
        }

        this.preventEventPropagationAndClearValue();
    }

    /**
     * Initializes accessibility features for the combo box.
     * Adds aria-labels to empty headers in group rows for better screen reader support.
     * @param comboBox - The combo box instance.
     */
    private accessibilityInit(comboBox: kendo.ui.MultiColumnComboBox): void {
        // Find group rows and add aria-labels to empty headers
        comboBox.list.find('.k-table-group-row').each((index: number, element: HTMLElement) => {
            const groupRow = $(element);
            if (groupRow) {
                groupRow.find('th').each((thIndex: number, thElement: HTMLElement) => {
                    const header = $(thElement);
                    if (header && !header.text().trim()) {
                        header.attr('aria-label', 'group row');
                    }
                });
            }
        });
    }

    /**
    * Prevents event propagation and clears the value manually.
    * Without this, the clear button needs to be pressed twice to clear the combo box saved values. Bug ID: #65564
    */
    private preventEventPropagationAndClearValue(): void {
        $(`input[name="${this.comboId}_input"] ~ .k-clear-value`).on('click', (event) => {
            event.stopPropagation();
            const comboBox = $(`#${this.comboId}`).data('kendoMultiColumnComboBox') as kendo.ui.MultiColumnComboBox | undefined;
            if (comboBox) {
                comboBox.value('');
                comboBox.text('');
                // Trigger the change event to notify validation
                comboBox.trigger('change');
                comboBox.element.trigger('change');
            }
        });
    }

    /**
     * Handles the filtering event of the combo box. Highlights the matching text in the combo box items.
     * @param e - The event object containing the sender and other event data.
     */
    private onFiltering(e: kendo.ui.MultiColumnComboBoxChangeEvent): void {
        const combo = e.sender;
        const items = combo?.list?.find('li[role="option"]');

        // Ensure the input value is a string
        const inputValue = combo?.input?.val();
        const inputText = typeof inputValue === 'string' ? inputValue.toLowerCase() : '';

        // Remove any existing highlights before applying new ones
        this.removeHighlight(combo);

        items.each((index: number, element: HTMLElement) => {
            const item = $(element); // Current list item
            const cells = item.find('span.k-table-td'); // Find all table cells within the item

            cells.each((cellIndex: number, cellElement: HTMLElement) => {
                const cell = $(cellElement); // Current cell
                const cleanText = cell?.text() || ""; // Extract the plain text (without HTML)

                // Find the position of the filtering text in the cell
                const startIndex = cleanText.toLowerCase().indexOf(inputText);

                if (startIndex >= 0 && inputText) {
                    // Match found, construct the highlighted HTML
                    const endIndex = startIndex + inputText.length;
                    const highlightedHtml = [
                        cleanText.slice(0, startIndex),
                        '<span class="fw-bold">', // Wrap the matched text in a span with the fw-bold class
                        cleanText.slice(startIndex, endIndex),
                        '</span>',
                        cleanText.slice(endIndex)
                    ].join('');

                    cell.html(highlightedHtml); // Update the cell's HTML content
                }
            });
        });
    }

    /**
     * Removes any existing highlights from the combo box items.
     * @param combo - The combo box instance.
     */
    private removeHighlight(combo: kendo.ui.MultiColumnComboBox): void {
        const items = combo?.list?.find('span.fw-bold');
        items?.each((index: number, element: HTMLElement) => {
            const cell = $(element);
            cell.contents().unwrap(); // Remove the span but keep the text
        });
    }
}